import { Tooltip } from '@mantine/core';
import { Info } from 'lucide-react';
import type { YearOverview } from '../../services/timeEntryService';

const APP_RELEASE_DATE = '2025-11-03';

interface OverviewKPIProps {
  yearOverview?: YearOverview;
  ytdProjektstunden: number;
  ytdSoll: number;
  ytdBillable: number;
  billableRate: number;
  selectedYear: number;
}

function formatDiff(diff: number) {
  return `(${diff > 0 ? '+' : ''}${diff.toFixed(1)}h)`;
}

export function OverviewKPI({
  yearOverview,
  ytdProjektstunden,
  ytdSoll,
  ytdBillable,
  billableRate,
  selectedYear,
}: OverviewKPIProps) {
  const totalProjectHours = yearOverview ? yearOverview.totalprojecthours : ytdProjektstunden;
  const totalBillableHours = yearOverview ? yearOverview.totalbillablehours : ytdBillable;
  const calculatedBillableRate = yearOverview && yearOverview.totalprojecthours > 0
    ? ((yearOverview.totalbillablehours / yearOverview.totalprojecthours) * 100)
    : billableRate;
  const ytdSollValue = yearOverview ? yearOverview.totalplannedtime : ytdSoll;
  const hoursDifference = totalProjectHours - ytdSollValue;
  const ytdLabel = `YTD Projektstunden vs. Soll (${
    selectedYear === new Date().getFullYear() ? 'dieses Jahr' : selectedYear
  })`;

  const overallStart = yearOverview?.overallstartdate;
  const overallIst = yearOverview?.overallprojecthours ?? 0;
  const overallSoll = yearOverview?.overallplannedtime ?? 0;
  const overallDiff = overallIst - overallSoll;
  const overallStartLabel = overallStart
    ? new Date(`${overallStart}T00:00:00`).toLocaleDateString('de-DE')
    : '–';

  return (
    <div className="ov-grid ov-grid-cols-1 ov-md:grid-cols-3 ov-gap-6">
      <div className="ov-bg-muted ov-rounded-lg ov-p-6 ov-border ov-border-gray-200 ov-shadow-sm">
        <div className="ov-text-gray-600 ov-text-sm ov-mb-2">
          Gesamt seit{' '}
          {overallStart === APP_RELEASE_DATE ? (
            <Tooltip
              label="Start der Erfassung in dieser App (Release 03.11.2025). Frühere Stunden werden nicht berücksichtigt."
              multiline
              w={260}
              withArrow
            >
              <span
                tabIndex={0}
                style={{ textDecoration: 'underline dotted', cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 2 }}
              >
                {overallStartLabel}
                <Info size={12} aria-hidden />
              </span>
            </Tooltip>
          ) : (
            overallStartLabel
          )}{' '}
          bis heute
        </div>
        {overallStart ? (
          <>
            <div className="ov-text-gray-900 ov-text-3xl ov-mb-2">{overallIst.toFixed(1)}h</div>
            <div className="ov-flex ov-items-center ov-gap-2">
              <div className="ov-text-gray-600 ov-text-sm">Soll: {overallSoll.toFixed(1)}h</div>
              <div className={`ov-text-sm ${overallDiff >= 0 ? 'ov-text-green-600' : 'ov-text-red-600'}`}>
                {formatDiff(overallDiff)}
              </div>
            </div>
          </>
        ) : (
          <div className="ov-text-gray-900 ov-text-3xl ov-mb-2">–</div>
        )}
      </div>

      <div className="ov-bg-muted ov-rounded-lg ov-p-6 ov-border ov-border-gray-200 ov-shadow-sm">
        <div className="ov-text-gray-600 ov-text-sm ov-mb-2">{ytdLabel}</div>
        <div className="ov-text-gray-900 ov-text-3xl ov-mb-2">
          {totalProjectHours.toFixed(1)}h
        </div>
        <div className="ov-flex ov-items-center ov-gap-2">
          <div className="ov-text-gray-600 ov-text-sm">Soll: {ytdSollValue.toFixed(1)}h</div>
          <div className={`ov-text-sm ${hoursDifference >= 0 ? 'ov-text-green-600' : 'ov-text-red-600'}`}>
            {formatDiff(hoursDifference)}
          </div>
        </div>
      </div>

      <div className="ov-bg-muted ov-rounded-lg ov-p-6 ov-border ov-border-gray-200 ov-shadow-sm">
        <div className="ov-text-gray-600 ov-text-sm ov-mb-2">Billable Rate</div>
        <div className="ov-text-gray-900 ov-text-3xl ov-mb-2">
          {calculatedBillableRate.toFixed(1)}%
        </div>
        <div className="ov-text-gray-600 ov-text-sm">
          {totalBillableHours.toFixed(1)}h billable von {totalProjectHours.toFixed(1)}h
        </div>
      </div>
    </div>
  );
}
